// src/pages/Browse.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import useLocale from '../hooks/useLocale';
import './browse.css'; // Or your chosen filename

const ITEMS_PER_PAGE = 25;

function Browse() {
    const { t } = useLocale(); // Correctly get t
    const { user } = useAuth();
    const loggedInUserId = user?.userId; // Get ID from user object, use optional chaining
    const isAdmin = !!user?.is_admin; // Get admin status, coerce to boolean

    console.log({isAdmin});




    // Data and Loading State
    const [images, setImages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter State
    const [targetFilter, setTargetFilter] = useState('');
    const [focalLengthFilter, setFocalLengthFilter] = useState('');
    const [activeFilters, setActiveFilters] = useState({ target: '', focalLength: '' });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    // Fetch images function
    const fetchImages = useCallback(async () => {
        if (!user) return;
        setIsLoading(true);
        setError(null);
        console.log(`[Browse] Fetching images - Page: ${currentPage}, Filters:`, activeFilters);
        const token = localStorage.getItem('authToken');
        if (!token) {
            // Maybe set an error state here using t()?
            setError(t('error_auth_token_required', 'Authentication token required.'));
            setIsLoading(false);
            return;
        }

        try {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: ITEMS_PER_PAGE.toString()
            });
            if (activeFilters.target) { params.append('target', activeFilters.target); }
            if (activeFilters.focalLength) { params.append('focalLength', activeFilters.focalLength); }

            const apiUrl = `/api/images?${params.toString()}`;
            console.log(`[Browse] Fetching from: ${apiUrl}`);

            const response = await fetch(apiUrl, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                let errorData = null;
                let errorCode = null;
                try {
                    errorData = await response.json();
                    errorCode = errorData?.errorCode;
                } catch (e) { console.warn("Non-JSON error response from /api/images"); }
                // Throw error with code if available, otherwise generic message
                const error = new Error(`HTTP error ${response.status}`);
                error.code = errorCode;
                throw error;
            }

            const data = await response.json();
            console.log("[Browse] Data received:", data);
            setImages(data.images || []);
            if (data.pagination) {
                setCurrentPage(data.pagination.currentPage);
                setTotalPages(data.pagination.totalPages);
                setTotalItems(data.pagination.totalItems);
            } else {
                setTotalPages(1);
                setTotalItems(data.images?.length || 0);
            }
        } catch (e) {
            console.error("Error fetching images:", e);
            const errorCode = e.code; // Check for code attached from !response.ok block
            const messageKey = errorCode || 'API_SERVER_ERROR_GENERIC'; // Use code or generic
            setError(t(messageKey, 'Error fetching images. Please try again.'));
            setImages([]);
            setTotalPages(0);
            setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
        // Corrected dependency array for fetchImages
    }, [user, currentPage, activeFilters, t]); // Depends on user, page, filters, and t (for error handling)

    // Effect to fetch images
    useEffect(() => {
        if (user) {
            fetchImages();
        } else {
            setIsLoading(false);
            setImages([]);
            setTotalPages(0);
            setTotalItems(0);
            setError(t('browse_error_login_required', 'Please log in to browse images.'));
        }
        // Corrected dependency array for useEffect
    }, [user, currentPage, activeFilters, fetchImages, t]); // Depends on fetchImages and t

    // Handlers for filters
    const handleApplyFilters = () => {
        setCurrentPage(1);
        setActiveFilters({
            target: targetFilter,
            focalLength: focalLengthFilter
        });
    };

    // Handlers for pagination
    const goToNextPage = () => {
        setCurrentPage(prevPage => Math.min(prevPage + 1, totalPages));
    };
    const goToPrevPage = () => {
        setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
    };

    // --- DOWNLOAD HANDLER ---
    const handleDownload = useCallback(async (image) => { // Wrap in useCallback
        const s3Key = image?.s3_key;
        if (!s3Key) {
            console.error("Missing s3_key in image object:", image);
            alert(t('download_error_missing_key_local', 'Cannot download: Missing file identifier.'));
            return;
        }
        console.log(`[Browse] Download requested for S3 Key: ${s3Key}`);
        try {
            const token = localStorage.getItem('authToken');
            if (!token) throw new Error("Local: Auth token missing");

            const response = await fetch(`/api/download?key=${encodeURIComponent(s3Key)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                let errorData = null; let errorCode = null;
                try { errorData = await response.json(); errorCode = errorData?.errorCode; } catch (e) { /* Ignore */ }
                const httpError = new Error(`HTTP error ${response.status}`);
                httpError.code = errorCode;
                throw httpError;
            }
            const data = await response.json();
            if (data.downloadUrl) {
                window.open(data.downloadUrl, '_blank');
            } else {
                throw new Error("Local: Download URL missing in API response");
            }
        } catch (err) {
            console.error("Download error details:", err);
            const backendErrorCode = err.response?.data?.errorCode || err.code;
            let messageKey = backendErrorCode || 'AUTH_ERROR_GENERIC_REQUEST_FAILED';
            // Optional checks for local messages can go here if desired
            alert(t(messageKey, 'Download failed. Please try again.'));
        }
    }, [t]); // Depends on t

    // --- DELETE HANDLER ---
    const handleDelete = useCallback(async (s3Key) => { // Wrap in useCallback
        if (!s3Key) return;
        console.log(`[Browse] Delete requested for: ${s3Key}`);
        const confirmDelete = window.confirm(
            `${t('delete_confirm_prompt', 'Are you sure you want to delete this file?')} \n\n${s3Key}`
        );
        if (confirmDelete) {
            console.log(`[Browse] User confirmed delete for: ${s3Key}`);
            try {
                const token = localStorage.getItem('authToken');
                if (!token) throw new Error("Local: Auth token missing"); // Use simple error

                const response = await fetch(`/api/files?key=${encodeURIComponent(s3Key)}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) {
                    let errorData = null; let errorCode = null;
                    try { errorData = await response.json(); errorCode = errorData?.errorCode; } catch (e) { /* Ignore */ }
                    // Use API_AUTH_FORBIDDEN if that was the code, otherwise generic
                    let errorMsg = t(errorCode || 'browse_error_delete_api', `Failed to delete file.`);
                    throw new Error(errorMsg); // Throw the user-facing message string
                }

                // Success case from API (should return messageCode)
                const data = await response.json();
                const messageCode = data?.messageCode || 'browse_delete_success'; // Use code or fallback key

                setImages(currentImages => currentImages.filter(image => image.s3_key !== s3Key));
                console.log(`[Browse] Successfully deleted ${s3Key}, removed from UI.`);
                alert(t(messageCode, 'File deleted successfully.')); // Use t() with code

            } catch (err) {
                console.error("Delete error details:", err);
                const backendErrorCode = err.response?.data?.errorCode || err.code; // Check response/attached code
                let messageKey = backendErrorCode;
                if (!messageKey) {
                    // Check for specific local errors if desired
                    if (err.message === "Local: Auth token missing") { messageKey = 'error_auth_token_required'; }
                    // Otherwise use generic fallback
                    else { messageKey = 'AUTH_ERROR_GENERIC_REQUEST_FAILED'; }
                }
                // Use t() for alert
                alert(t(messageKey, 'Error deleting file. Please try again.')); // Generic final fallback
            }
        } else {
            console.log(`[Browse] User cancelled delete for: ${s3Key}`);
        }
    }, [t, setImages]); // Depends on t and setImages

    // --- Rendering Logic ---
    return (
        <div className="browse-page-container">
            <h1>{t('browse_title', 'Browse Images')}</h1>
            {!user && error && <p className="error-text">{error}</p>}

            {user && (
                <>
                    {/* --- Filter Section --- */}
                    <div className="filter-section">
                        {/* Use correct standardized keys */}
                        <h3>{t('browse_filter_title', 'Narrow your search:')}</h3>
                        <div className="filter-inputs">
                            <label htmlFor="target-filter">{t('browse_filter_target_label', 'Target:')} </label>
                            <input id="target-filter" type="text" value={targetFilter} onChange={(e) => setTargetFilter(e.target.value)} placeholder={t('browse_filter_target_placeholder', 'e.g., M31')} />
                            <label htmlFor="fl-filter">{t('browse_filter_focal_length_label', 'Focal Length (mm): ')} </label>
                            <input id="fl-filter" type="number" value={focalLengthFilter} onChange={(e) => setFocalLengthFilter(e.target.value)} placeholder={t('browse_filter_focal_length_placeholder', 'e.g., 1000')} />
                            <button onClick={handleApplyFilters} disabled={isLoading} className="button-apply-filters">
                                {t('browse_apply_filters_button', 'Apply Filters')}
                            </button>
                        </div>
                        {error && <p className="error-text">{t('common_error_prefix', 'Error:')} {error}</p>}
                    </div>

                    {isLoading ? (
                        <div>{t('browse_images_loading', 'Loading Images...')}</div>
                    ) : images.length === 0 ? (
                        <p>{t('browse_no_images_message', 'No images match your selected criteria')}</p>
                    ) : (
                        <>
                            {/* Corrected key */}
                            <p>{totalItems} {t('browse_images_found_text', 'image(s) found')}</p>
                            {/* --- Pagination Controls --- */}
                            <div className="pagination-controls">
                                {/* Corrected keys */}
                                <span>{t('browse_pagination_page', 'Page')} {currentPage} {t('browse_pagination_of', 'of')} {totalPages}</span>
                                <button onClick={goToPrevPage} disabled={currentPage <= 1 || isLoading}>
                                    {/* Corrected key */}
                                    {t('common_previous', 'Previous')}
                                </button>
                                <button onClick={goToNextPage} disabled={currentPage >= totalPages || isLoading}>
                                    {/* Corrected key */}
                                    {t('common_next', 'Next')}
                                </button>
                            </div>
                            {/* --- End Pagination Controls --- */}
                            <table className="image-table">
                                <thead>{/* Add TH elements with t() calls for column headers */}</thead>
                                <tbody>
                                    {images.map((image) => (

                                        <tr key={image.image_id}>
                                            <td>{image.target_name || 'N/A'}</td>
                                            <td className="image-filename">{image.original_filename || 'N/A'}</td>
                                            <td>{image.filters_used || 'N/A'}</td>
                                            <td>{image.focal_length ? `${image.focal_length}mm` : 'N/A'}</td>
                                            <td>{image.exposure_time ? `${image.exposure_time}s` : 'N/A'}</td>
                                            <td className="image-actions">
                                                <button onClick={() => handleDownload(image)} className="button-link button-download" title={`Download ${image.original_filename}`}>
                                                    {/* Corrected key */}
                                                    {t('browse_download_button', 'Download')}
                                                </button>

                                                {/* --- Verify this conditional logic exists and is saved --- */}
                                                {loggedInUserId && (image.user_id === loggedInUserId || isAdmin) && (
                                                    <button
                                                        onClick={() => handleDelete(image.s3_key)}
                                                        className="button-link button-delete"
                                                        title={`Delete ${image.original_filename}`}
                                                    >
                                                        {t('browse_delete_button', 'Delete')}
                                                    </button>
                                                )}
                                                {/* --- End Conditional Delete Button --- */}


                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                    {/* --- End Results Section --- */}
                </>
            )} {/* End user check */}
        </div> // End browse-page-container
    ); // End return
} // End Browse component

export default Browse;